import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, AlertCircle, Key, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isWarning, setIsWarning] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsWarning(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const errorDetail = err.response?.data?.detail || '';
      
      // Handle brute force protection messages
      if (errorDetail === 'warning_attempt_3') {
        setError(t('auth.warningAttempt3'));
        setIsWarning(true);
      } else if (errorDetail === 'warning_attempt_4') {
        setError(t('auth.warningAttempt4'));
        setIsWarning(true);
      } else if (errorDetail === 'account_locked_bruteforce') {
        setError(t('auth.accountLockedBruteforce'));
      } else if (errorDetail.startsWith('account_locked:')) {
        const minutes = errorDetail.split(':')[1];
        setError(t('auth.accountLockedMinutes', { minutes }));
      } else if (errorDetail === 'account_disabled') {
        setError(t('auth.accountDisabled'));
      } else if (errorDetail === 'invalid_credentials') {
        setError(t('auth.loginError'));
      } else {
        setError(t('auth.loginError'));
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="login-page">
      <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-md">
        <h1 className="font-serif text-3xl font-bold text-primary text-center mb-8">{t('auth.loginTitle')}</h1>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">{t('auth.email')}</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">{t('auth.password')}</label>
            <div className="relative">
              <Input 
                type={showPassword ? "text" : "password"} 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
                data-testid="login-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-gold hover:underline" style={{ color: '#D4A574' }} data-testid="forgot-password-link">
              {t('auth.forgotPassword')}
            </Link>
          </div>
          {error && (
            <div className={`p-3 rounded-lg text-sm ${isWarning ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {error}
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-gold hover:bg-gold/90 text-white" style={{ backgroundColor: '#D4A574' }} data-testid="login-submit">
            {loading ? t('common.loading') : t('auth.loginButton')}
          </Button>
        </form>

        <p className="text-center text-secondary mt-6">
          {t('auth.noAccount')} <Link to="/register" className="text-gold hover:underline" style={{ color: '#D4A574' }}>{t('nav.register')}</Link>
        </p>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuthStore();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', name: '', language: 'es' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation checks
  const passwordChecks = {
    minLength: formData.password.length >= 8,
    hasLetter: /[A-Za-z]/.test(formData.password),
    hasNumber: /\d/.test(formData.password),
    passwordsMatch: formData.password === formData.confirmPassword && formData.confirmPassword.length > 0,
  };
  const isPasswordValid = passwordChecks.minLength && passwordChecks.hasLetter && passwordChecks.hasNumber && passwordChecks.passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailExists(false);
    
    // Client-side password validation
    if (!passwordChecks.minLength || !passwordChecks.hasLetter || !passwordChecks.hasNumber) {
      setError(t('auth.passwordNotMeetRequirements'));
      return;
    }
    
    // Check passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch', 'Las contraseñas no coinciden'));
      return;
    }
    
    // Terms and conditions validation
    if (!acceptedTerms) {
      setError(t('auth.mustAcceptTerms'));
      return;
    }
    
    setLoading(true);
    
    try {
      await register(formData.email, formData.password, formData.name, formData.language);
      navigate('/');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const statusCode = err.response?.status;
      
      // Handle specific error cases
      if (errorDetail?.code === 'EMAIL_EXISTS' || statusCode === 409) {
        setEmailExists(true);
      } else if (statusCode === 422) {
        // Validation error from backend
        if (typeof errorDetail === 'string') {
          if (errorDetail.toLowerCase().includes('password')) {
            setError(t('auth.passwordMinRequirements'));
          } else if (errorDetail.toLowerCase().includes('email')) {
            setError(t('auth.invalidEmailFormat'));
          } else {
            setError(errorDetail);
          }
        } else if (Array.isArray(errorDetail)) {
          // Pydantic validation errors
          const messages = errorDetail.map(e => {
            if (e.loc?.includes('password')) {
              return t('auth.password') + ': ' + e.msg;
            } else if (e.loc?.includes('email')) {
              return t('auth.email') + ': ' + e.msg;
            }
            return e.msg;
          });
          setError(messages.join('. '));
        } else {
          setError(t('auth.validationError'));
        }
      } else if (statusCode === 400) {
        setError(errorDetail || t('auth.invalidData'));
      } else {
        setError(t('auth.registerError'));
      }
    }
    setLoading(false);
  };

  const handleSendNewPassword = async () => {
    setSendingReset(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { 
        email: formData.email 
      });
      setResetSent(true);
    } catch (err) {
      setError(t('auth.errorSendingPassword'));
    }
    setSendingReset(false);
  };

  const handleChangeEmail = () => {
    setEmailExists(false);
    setFormData({ ...formData, email: '' });
  };

  // Show success message after sending reset
  if (resetSent) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="font-serif text-2xl font-bold text-primary mb-4">{t('auth.checkYourEmail')}</h2>
          <p className="text-secondary mb-6">
            {t('auth.newPasswordSentTo')} <strong>{formData.email}</strong>
          </p>
          <Link to="/login">
            <Button className="w-full bg-gold hover:bg-gold/90 text-white" style={{ backgroundColor: '#D4A574' }}>
              {t('auth.goToLogin')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="register-page">
      <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-md">
        <h1 className="font-serif text-3xl font-bold text-primary text-center mb-8">{t('auth.registerTitle')}</h1>
        
        {/* Email Exists Warning */}
        {emailExists && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">{t('auth.emailAlreadyRegistered')}</p>
                <p className="text-sm text-amber-700 mt-1">
                  {t('auth.accountExistsWithEmail')} <strong>{formData.email}</strong>
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleSendNewPassword}
                disabled={sendingReset}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Key className="h-4 w-4 mr-2" />
                {sendingReset ? t('auth.sending') : t('auth.sendNewPassword')}
              </Button>
              <Button 
                onClick={handleChangeEmail}
                variant="outline"
                className="w-full"
              >
                {t('auth.useAnotherEmail')}
              </Button>
            </div>
          </div>
        )}
        
        {!emailExists && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">{t('auth.name')}</label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="register-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">{t('auth.email')}</label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="register-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">{t('auth.password')}</label>
              <div className="relative">
                <Input 
                  type={showPassword ? "text" : "password"}
                  value={formData.password} 
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  required 
                  className="pr-10"
                  data-testid="register-password" 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {/* Password Requirements */}
              {(passwordFocused || formData.password.length > 0) && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-2">{t('auth.passwordRequirements')}</p>
                  <ul className="space-y-1">
                    <li className={`text-xs flex items-center gap-2 ${passwordChecks.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${passwordChecks.minLength ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {passwordChecks.minLength ? '✓' : '○'}
                      </span>
                      {t('auth.minCharacters')}
                    </li>
                    <li className={`text-xs flex items-center gap-2 ${passwordChecks.hasLetter ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${passwordChecks.hasLetter ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {passwordChecks.hasLetter ? '✓' : '○'}
                      </span>
                      {t('auth.atLeastOneLetter')}
                    </li>
                    <li className={`text-xs flex items-center gap-2 ${passwordChecks.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${passwordChecks.hasNumber ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {passwordChecks.hasNumber ? '✓' : '○'}
                      </span>
                      {t('auth.atLeastOneNumber')}
                    </li>
                  </ul>
                </div>
              )}
            </div>
            
            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">{t('auth.confirmPassword', 'Confirmar Contraseña')}</label>
              <div className="relative">
                <Input 
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword} 
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} 
                  required 
                  className="pr-10"
                  data-testid="register-confirm-password" 
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {/* Password Match Indicator */}
              {formData.confirmPassword.length > 0 && (
                <div className={`mt-2 text-xs flex items-center gap-2 ${passwordChecks.passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${passwordChecks.passwordsMatch ? 'bg-green-100' : 'bg-red-100'}`}>
                    {passwordChecks.passwordsMatch ? '✓' : '✗'}
                  </span>
                  {passwordChecks.passwordsMatch 
                    ? t('auth.passwordsMatch', 'Las contraseñas coinciden')
                    : t('auth.passwordsDoNotMatch', 'Las contraseñas no coinciden')
                  }
                </div>
              )}
            </div>
            
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {error}
                </p>
              </div>
            )}
            
            {/* Age and Terms Checkbox */}
            <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
              <input
                type="checkbox"
                id="accept-terms-register"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-gold focus:ring-gold"
                style={{ accentColor: '#D4A574' }}
                data-testid="accept-terms-register"
              />
              <label htmlFor="accept-terms-register" className="text-sm text-secondary cursor-pointer">
                {t('auth.termsCheckbox')}{' '}
                <Link to="/terms" target="_blank" className="text-gold hover:underline font-medium" style={{ color: '#D4A574' }}>
                  {t('auth.termsLink')}
                </Link>
                {' '}{t('auth.andThe')}{' '}
                <Link to="/privacy" target="_blank" className="text-gold hover:underline font-medium" style={{ color: '#D4A574' }}>
                  {t('auth.privacyLink')}
                </Link>.
              </label>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading || !isPasswordValid} 
              className="w-full bg-gold hover:bg-gold/90 text-white disabled:opacity-50" 
              style={{ backgroundColor: '#D4A574' }} 
              data-testid="register-submit"
            >
              {loading ? t('common.loading') : t('auth.registerButton')}
            </Button>
          </form>
        )}

        <p className="text-center text-secondary mt-6">
          {t('auth.hasAccount')} <Link to="/login" className="text-gold hover:underline" style={{ color: '#D4A574' }}>{t('nav.login')}</Link>
        </p>
      </div>
    </div>
  );
}
